import React, { useEffect, useRef, useState } from 'react'
import { Grid, generateSrcSet, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { QuoteFile } from 'paintscout'
import { ImgComparisonSlider } from '@img-comparison-slider/react'
import type { PresentationBlockProps } from '../..'
import compareBlockProps from '../../util/compareBlockProps'
import { InlineEditableMedia, getObjectPosition } from '../../inlineEditable'
import { usePresentation } from '../../../context'
import { getSliderAspectRatio } from './getSliderAspectRatio'
import { isPointInRect } from './isPointInRect'

// QuoteFile.height is optional — used when either image is missing dimension data so the
// slider doesn't collapse to a zero/NaN height. Only used as a fallback when neither image has
// an explicit `presentationOptions.aspectRatio` set (see getSliderAspectRatio).
const DEFAULT_SLIDER_HEIGHT = 400

const useStyles = makeStyles<Theme, { maxHeight: number; aspectRatio: string | null }>(
  (_theme) => ({
    root: {},
    slider: ({ maxHeight, aspectRatio }) => ({
      justifyContent: 'center',
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      ...(aspectRatio ? { aspectRatio, height: 'auto', maxHeight: 'none' } : { maxHeight, height: maxHeight }),
      '&:focus': {
        outline: 'none'
      }
    }),
    image: ({ maxHeight, aspectRatio }) => ({
      width: '100%',
      maxWidth: '100%',
      objectFit: 'cover',
      ...(aspectRatio ? { aspectRatio, height: '100%', maxHeight: 'none' } : { maxHeight, height: maxHeight })
    }),
    svg: { margin: '-20px' }
  }),
  { name: 'SliderImageBlock' }
)

function SliderImageBlock(props: PresentationBlockProps) {
  const {
    isEditable,
    isSettings,
    presentationIndustry,
    block,
    blockPrefix,
    setFieldValue,
    setDragSuspended,
    submitForm,
    WrapperComponent
  } = props
  const { presentationLibrary } = usePresentation()
  const { fields } = block
  const { leftImage, rightImage } = fields ?? {}
  const [isHoveringSlider, setIsHoveringSlider] = useState(false)
  const sliderGridRef = useRef<HTMLDivElement>(null)

  const handleEditField = (field: string, content: QuoteFile) => {
    setFieldValue(`${blockPrefix}.${field}`, content)
    submitForm()
  }

  // Safety valve for the onMouseLeave below: if the divider is dragged fast enough that the
  // cursor exits the Grid without a mouseleave ever registering, releasing the mouse anywhere
  // still resumes this block's drag handle instead of leaving it stuck suspended. Only fires
  // when the cursor is actually outside the Grid's bounds — otherwise a normal, completed
  // divider drag (mouse released while still hovering) would re-enable the block's drag handle
  // immediately, since no further mouseenter/mouseleave crosses to re-suspend it (ENG-1520).
  useEffect(() => {
    if (!isHoveringSlider) return
    const handleMouseUp = (ev: MouseEvent) => {
      // `sliderGridRef.current` is only null if this Grid unmounted (e.g. isEditable flipped)
      // while still marked as hovering — isPointInRect's null-rect fallback of `false` is the
      // correct behaviour there too, since there's nothing left to still be "inside" of.
      const rect = sliderGridRef.current?.getBoundingClientRect()
      if (isPointInRect({ x: ev.clientX, y: ev.clientY }, rect)) return
      setIsHoveringSlider(false)
      setDragSuspended?.(false)
    }
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [isHoveringSlider, setDragSuspended])

  return (
    <WrapperComponent {...props}>
      <Grid container spacing={3} style={{ justifyContent: 'center' }}>
        {isEditable && (
          <>
            <Grid item md={6} sm={6} xs={12}>
              <InlineEditableMedia
                mediaOptionsProps={{ blockPrefix, fieldSuffix: 'leftImage' }}
                file={leftImage}
                handleEdit={(file) => handleEditField('fields.leftImage', file)}
                isEditable={isEditable}
                isSettings={isSettings}
                placeholderButtonText={'Add Before Image'}
                presentationIndustry={presentationIndustry}
                presentationLibrary={presentationLibrary}
                youtube={false}
              />
            </Grid>
            <Grid item md={6} sm={6} xs={12}>
              <InlineEditableMedia
                mediaOptionsProps={{ blockPrefix, fieldSuffix: 'rightImage' }}
                file={rightImage}
                handleEdit={(file) => handleEditField('fields.rightImage', file)}
                isEditable={isEditable}
                isSettings={isSettings}
                placeholderButtonText={'Add After Image'}
                presentationIndustry={presentationIndustry}
                presentationLibrary={presentationLibrary}
                youtube={false}
              />
            </Grid>
          </>
        )}
        {!isEditable && leftImage && rightImage && (
          <Grid
            ref={sliderGridRef}
            item
            md={12}
            sm={12}
            xs={12}
            // The comparison slider's handle fires a native click when a divider drag ends.
            // Left alone, that click bubbles to Presentation.tsx's block-selection handler and
            // flips this block into its two-image edit view mid-interaction (ENG-1520).
            onClick={(ev) => ev.stopPropagation()}
            onMouseEnter={() => {
              setIsHoveringSlider(true)
              setDragSuspended?.(true)
            }}
            onMouseLeave={() => {
              setIsHoveringSlider(false)
              setDragSuspended?.(false)
            }}
          >
            <SliderImageContainer leftImage={leftImage} rightImage={rightImage} />
          </Grid>
        )}
      </Grid>
    </WrapperComponent>
  )
}

const SliderImageContainer = React.memo(function SliderImageContainer({
  leftImage,
  rightImage
}: {
  leftImage: QuoteFile
  rightImage: QuoteFile
}) {
  const generatedLeftSrc = generateSrcSet({ file: leftImage })
  const generatedRightSrc = generateSrcSet({ file: rightImage })
  const leftHeight = leftImage.height ?? rightImage.height ?? DEFAULT_SLIDER_HEIGHT
  const rightHeight = rightImage.height ?? leftImage.height ?? DEFAULT_SLIDER_HEIGHT
  const maxHeight = Math.min(leftHeight, rightHeight)
  const aspectRatio = getSliderAspectRatio({ leftImage, rightImage })
  const classes = useStyles({ maxHeight, aspectRatio })

  return (
    <ImgComparisonSlider className={classes.slider}>
      <svg
        // @ts-ignore
        slot="handle"
        xmlns="http://www.w3.org/2000/svg"
        width="100"
        viewBox="-8 -3 16 6"
        className={classes.svg}
      >
        <path
          stroke="#fff"
          d="M -5 -2 L -7 0 L -5 2 M -5 -2 L -5 2 M 5 -2 L 7 0 L 5 2 M 5 -2 L 5 2"
          strokeWidth="1"
          fill="#fff"
          vectorEffect="non-scaling-stroke"
        ></path>
      </svg>
      <img
        slot="first"
        src={leftImage.src}
        alt={(leftImage as any).alt}
        srcSet={generatedLeftSrc}
        className={classes.image}
        style={aspectRatio ? { objectPosition: getObjectPosition(leftImage) } : undefined}
      />
      <img
        slot="second"
        src={rightImage.src}
        alt={(rightImage as any).alt}
        srcSet={generatedRightSrc}
        className={classes.image}
        style={aspectRatio ? { objectPosition: getObjectPosition(rightImage) } : undefined}
      />
    </ImgComparisonSlider>
  )
})

export default React.memo(SliderImageBlock, (prevProps, nextProps) => {
  return compareBlockProps(prevProps, nextProps)
})
